import { action } from 'typesafe-actions';
import { useLocalStorage } from 'react-use';
import React, { FC, useCallback, useReducer, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { Box } from '@dxb-web-ui-framework/design-system';
import { cardService } from '../../../../../channels/secure-web/services';
import { ECOMMBXLoader } from '../../../../content-composer/src/components/Generic/ECOMMBXLoader/ECOMMBXLoader';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../userProfile/src/contexts/UserInfo/hooks';
import { useCardHeaders, useIsCardAdmin } from '../../../../transaction/src/contexts/Parameters/hooks';
import { CardInfoListResponseBody } from '../../types';
import { addTargetFilterParams } from '../../utils/addTargetFilterParams';
import { defaultCardsProviderState } from './defaultState';
import { CardsReducer } from './CardsReducer';
import { CardsContext, CardsDispatchContext } from './CardsContext';
import { CardContextActionTypes } from './types';
import { updatePopularCards } from './utils';

const CardsProvider: FC<{ isCorporate?: boolean; isCardHolderDashboard?: boolean }> = ({
  children,
  isCorporate = false,
  isCardHolderDashboard = false,
}) => {
  const { t } = useTranslation();
  const customerId = useSelectedCustomerId();
  const cardHeaders = useCardHeaders();
  const isCardAdmin = useIsCardAdmin();
  // TODO: create separate flag for corporate / individual entitlement
  const isCardsAllowed = useUserInfoIsUserAllowedAction(
    [UserAppTransaction.PrepaidCardList, UserAppTransaction.CorporateCardList],
    'OR',
  );
  // Get popular cards from local storage
  const [popularCards, setPopularCards] = useLocalStorage<GenericObject>('popular-cards', {});
  // Get initial state values
  const [state, dispatch] = useReducer(CardsReducer, defaultCardsProviderState({ popularCards }));
  const { cardId } = useParams();

  const shouldFetchIndividualCards = !isEmpty(cardHeaders) && !isEmpty(customerId) && isCardsAllowed && !isCorporate;
  const shouldFetchCorporateCards =
    !isEmpty(cardHeaders) && !isEmpty(customerId) && isCardsAllowed && isCorporate && isCardAdmin;

  const {
    data: individualCardListData,
    error: individualCardListDataError,
    isLoading: isLoadingIndividualCardListData,
    mutate: refetchIndividualCardsDataMutation,
  } = useInvokeSWRService<CardInfoListResponseBody>(
    cardService.GetIndividualCards,
    {
      headers: cardHeaders,
      customerId,
      pagination: {
        pageNumber: state.pagination.pageNumber ?? 0,
        pageSize: cardId ? 50 : state.pagination.pageSize,
      },
      meta: state.meta,
      filter: addTargetFilterParams(state.cardsListFilters, t),
    },
    shouldFetchIndividualCards,
  );

  const {
    data: corporateCardListData,
    error: corporateCardListDataError,
    isLoading: isLoadingCorporateCardListData,
    mutate: refetchCorporateCardsDataMutation,
  } = useInvokeSWRService<CardInfoListResponseBody>(
    cardService.GetCorporateCards,
    {
      headers: cardHeaders,
      customerId,
      pagination: {
        pageNumber: state.pagination.pageNumber ?? 0,
        pageSize: cardId ? 50 : state.pagination.pageSize,
      },
      meta: state.meta,
      filter: addTargetFilterParams(state.cardsListFilters, t, isCardHolderDashboard),
    },
    shouldFetchCorporateCards,
  );

  const cardListData = individualCardListData || corporateCardListData;
  const isLoadingCardListData = isLoadingIndividualCardListData || isLoadingCorporateCardListData;
  const cardListDataError = individualCardListDataError || corporateCardListDataError;

  const refetchCardsDataMutation = useMemo(() => {
    if (isCorporate) {
      return refetchCorporateCardsDataMutation;
    }
    return refetchIndividualCardsDataMutation;
  }, [isCorporate, refetchCorporateCardsDataMutation, refetchIndividualCardsDataMutation]);

  // Set popular cards
  const onSetPopularCards = useCallback(
    (customerId, cardId, popularCards) => {
      if (cardListData?.cards.find(card => card.cardId === cardId)) {
        const newPopularCards = updatePopularCards(customerId, cardId, popularCards);
        setPopularCards(newPopularCards);
      }
    },
    [cardListData, setPopularCards],
  );

  const onSetPagination = useCallback(
    (pageSize: number, pageNumber?: number) => {
      dispatch(
        action(CardContextActionTypes.SetPagination, {
          pagination: {
            pageNumber,
            pageSize,
            totalPages: cardListData?.pagination?.totalPages,
          },
          timestamp: new Date().toISOString(),
        }),
      );
    },
    [cardListData?.pagination?.totalPages],
  );

  // Callbacks for handling Consolidated Transactions filters and selected transaction
  const onCardsListFiltersChange = useCallback((filters, nextPage: number = 1, totalPages: number = 1) => {
    const newFilters = [
      { key: 'paymentCardStatuses', value: filters.paymentCardStatuses },
      { key: 'cardNumberSuffix', value: filters.cardNumberSuffix },
      { key: 'cardholderIdentification', value: filters.cardholderIdentification },
    ].reduce((acc, { key, value }) => {
      if (value?.toString()) {
        acc[key] = value;
      }
      return acc;
    }, {} as GenericObject);
    dispatch(
      action(CardContextActionTypes.SetCardsListFilters, {
        ...newFilters,
      }),
    );
  }, []);
  const shouldRenderChildren =
    (!isEmpty(cardListData) && !isLoadingCardListData) || (!!cardId && !isLoadingCardListData);

  return (
    <CardsContext.Provider
      value={{
        ...state,
        cardListData: cardListData?.cards,
        pagination: cardListData?.pagination || state.pagination,
        loadingState: {
          isLoadingCardListData,
        },
        errorState: {
          cardListDataError,
        },
        isCorporate: isCorporate ?? false,
        onSetPopularCards,
        onSetPagination,
        refetchCardsDataMutation,
        onCardsListFiltersChange,
      }}>
      <CardsDispatchContext.Provider value={dispatch}>
        {isLoadingCardListData && (
          <Box style={{ height: '100vh', position: 'absolute', top: '50%', right: '50%' }}>
            <ECOMMBXLoader size='full' verticalSpacing='large' />
          </Box>
        )}
        {shouldRenderChildren && children}
      </CardsDispatchContext.Provider>
    </CardsContext.Provider>
  );
};
export { CardsProvider };
